// src/utils/analytics.js
// GA4 の計測をここに集める。イベントの一覧と GA の管理画面の設定は docs/analytics.md。
//
// - page_view を含めてすべて手動で送る（プラグインは gtag の読み込みだけに使う。gatsby-config.js の exclude）
// - FANZA へのリンクのクリックは、部品ごとに onClick を書かず、文書全体で1か所で拾う（initClickTracking）。
//   中クリックも数えられて、リンクを増やしても計測の書き漏れが起きない。部品の側は data-ga-* を足すだけ
// - パラメータ名に source / medium / campaign / term / content を使わない。セッションの参照元の集計を汚す
//   （VRCFinder で実際に起きた）。発生した場所は origin_surface、操作の種類は interaction_type で送る

const MAX_VALUE = 100 // GA4 のパラメータの値の上限（標準プロパティ）

// page_type の語彙。呼び出し元で自由な文字列を増やさず、ここだけで管理する
const PAGE_TYPE_PATTERNS = [
  [/^\/$/, "home"],
  [/^\/blog\/(category|tag)\//, "blog_list"],
  [/^\/blog\/(\d+\/?)?$/, "blog_list"],
  [/^\/blog\/[^/]+\/fanza-doujin-sales-/, "blog_monthly"],
  [/^\/blog\/[^/]+\/fanza-doujin-weekly-/, "blog_weekly"],
  [/^\/blog\//, "blog_article"],
  [/^\/bookList(\/|$)/i, "work_list"],
  [/^\/genre\/?$/, "genre_search"],
  [/^\/genre\//, "genre_list"],
  [/^\/artist\/?$/, "artist_search"],
  [/^\/artist\//, "artist_page"],
  [/^\/archive(\/|$)/, "archive"],
  [/^\/analysis\/?$/, "tool_index"],
  [/^\/analysis\//, "tool"],
  [/^\/feature\/?$/, "feature_index"],
  [/^\/feature\//, "feature"],
  [/^\/(about|changelog|disclaimer)\/?$/, "static"],
]

export const getPageType = path => {
  const p = typeof path === "string" ? path : ""
  const hit = PAGE_TYPE_PATTERNS.find(([re]) => re.test(p))
  return hit ? hit[1] : "other"
}

const clip = value =>
  value === undefined || value === null
    ? undefined
    : String(value).slice(0, MAX_VALUE)

export const trackEvent = (name, params = {}) => {
  if (typeof window === "undefined") return
  const merged = { page_type: getPageType(window.location.pathname), ...params }
  Object.keys(merged).forEach(key => {
    if (merged[key] === undefined) delete merged[key]
  })
  // gtag は本番のビルドにしか入らないので、開発中はコンソールで確かめる
  if (process.env.NODE_ENV === "development") {
    console.debug("[ga]", name, merged)
  }
  if (typeof window.gtag === "function") window.gtag("event", name, merged)
}

export const trackPageView = location => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return
  window.gtag("event", "page_view", {
    page_title: document.title,
    page_location: location.href,
    page_type: getPageType(location.pathname),
  })
}

// ---------------------------------------------------------------------------
// クリックの計測（文書全体で1か所）
// ---------------------------------------------------------------------------

// affiliate.dmm.com（フッターの「Web Service by FANZA」）は作品へのリンクではないので dmm.com は含めない
const FANZA_HOST = /(^|\.)(dmm\.co\.jp|fanza\.co\.jp)$/

/**
 * FANZA へのリンクを持つカードに付ける属性。リンクの親のどこかに付いていればよい。
 * @param {string} surface 発生した面（docs/analytics.md の origin_surface の表）
 * @param {string} [title] 作品名
 * @param {number} [position] 順位。無ければ同じ面の中の並び順を送る
 */
export const fanzaSurface = (surface, title, position) => ({
  "data-ga-surface": surface,
  "data-ga-title": clip(title),
  "data-ga-position": position || undefined,
})

// アフィリエイトリンクの中の作品の URL から商品 ID を取り出す。
// 同人は …/detail/=/cid=d_123456/、FANZA ブックスは /product/12345/abc012345/
const contentIdFromUrl = url => {
  const target = url.searchParams.get("lurl") || url.href
  const match =
    target.match(/cid=([^/&?#]+)/) || target.match(/\/product\/\d+\/([^/?#]+)/)
  return match ? match[1] : undefined
}

const sendFanzaClick = (anchor, url) => {
  const card = anchor.closest("[data-ga-surface]")
  const surface = card?.dataset.gaSurface || "other"
  const title = card?.dataset.gaTitle
  // 作品のカードなら、同じ面の中で何番目かを送る（記事の本文の中のリンクなどには順番がない）
  const order = title
    ? Array.from(
        document.querySelectorAll(`[data-ga-surface="${surface}"]`)
      ).indexOf(card) + 1
    : undefined
  trackEvent("fanza_click", {
    content_id: contentIdFromUrl(url),
    work_title: title,
    origin_surface: surface,
    link_part:
      anchor.dataset.gaPart ||
      (anchor.querySelector("img, picture") ? "image" : "text"),
    position: Number(card?.dataset.gaPosition) || order || undefined,
    list_name: anchor.closest("[data-ga-list]")?.dataset.gaList,
  })
}

const readablePath = url => {
  const path = url.pathname + url.search
  try {
    return decodeURI(path)
  } catch {
    return path
  }
}

const sendNavigate = (anchor, url, section) => {
  const internal = url.origin === window.location.origin
  // 同じページの中の移動（目次の #見出し）は数えない
  if (
    internal &&
    url.pathname === window.location.pathname &&
    url.search === window.location.search
  ) {
    return
  }
  const label =
    anchor.getAttribute("aria-label") || anchor.textContent.trim() || undefined
  trackEvent("navigate", {
    section,
    target: clip(internal ? readablePath(url) : url.hostname),
    label: clip(label),
  })
}

const onLinkClick = event => {
  // 中クリックは auxclick で届く（右クリックなどは数えない）
  if (event.type === "auxclick" && event.button !== 1) return
  const anchor = event.target.closest?.("a[href]")
  if (!anchor) return
  let url
  try {
    url = new URL(anchor.href)
  } catch {
    return
  }
  if (FANZA_HOST.test(url.hostname)) {
    sendFanzaClick(anchor, url)
    return
  }
  const section = anchor.closest("[data-ga-section]")?.dataset.gaSection
  if (section) sendNavigate(anchor, url, section)
}

let clickTrackingStarted = false

/** gatsby-browser.js から1回だけ呼ぶ。capture で拾うので、部品の stopPropagation に影響されない */
export const initClickTracking = () => {
  if (clickTrackingStarted || typeof document === "undefined") return
  clickTrackingStarted = true
  document.addEventListener("click", onLinkClick, true)
  document.addEventListener("auxclick", onLinkClick, true)
}

// ---------------------------------------------------------------------------
// 部品から呼ぶイベント
// ---------------------------------------------------------------------------

/**
 * 検索（GA4 の推奨イベント。search_term は標準のディメンションになる）
 * @param {string} term 検索語
 * @param {"artist"|"genre"} searchType
 * @param {number} resultCount 件数。0 は「探されているのに見つからない語」
 * @param {string} urlParam 検索語を入れる URL のパラメータ名。一致すればリンクから来た検索
 */
export const trackSearch = (term, searchType, resultCount, urlParam) => {
  const fromLink =
    new URLSearchParams(window.location.search).get(urlParam) === term
  trackEvent("search", {
    search_term: clip(term),
    search_type: searchType,
    interaction_type: fromLink ? "link" : "form",
    result_count: resultCount,
  })
}

/** 分析ツールやタブの操作。自動で切り替わるタブなどは数えず、人が押したときだけ呼ぶ */
export const trackToolUse = (toolName, toolAction, toolValue) => {
  trackEvent("tool_use", {
    tool_name: toolName,
    tool_action: toolAction,
    tool_value: clip(toolValue),
  })
}

/** 記事のサンプル画像を拡大した */
export const trackSampleOpen = (contentId, title) => {
  trackEvent("sample_open", {
    content_id: clip(contentId),
    work_title: clip(title),
  })
}

/**
 * @param {"view"|"enter"|"leave"} action
 * @param {Function} [onSent] 送り終わったら呼ぶ（このあとページを離れるとき用。指定すると gtag がすぐ送る）
 */
export const trackAgeGate = (action, onSent) => {
  trackEvent("age_gate", { age_gate_action: action, event_callback: onSent })
}

export const trackPageNotFound = () => {
  let referrerType = "none"
  try {
    if (document.referrer) {
      referrerType =
        new URL(document.referrer).origin === window.location.origin
          ? "internal"
          : "external"
    }
  } catch {
    // 読めない参照元は none のまま
  }
  trackEvent("page_not_found", {
    page_type: "not_found",
    referrer_type: referrerType,
  })
}
